import { Flex, Text } from '@backend/lib-design-system';
import { DollarCircleFilledIcon, LocationFilledIcon } from '@backend/lib-design-system/icons';
import { Features } from '@backend/lib-feature-mgr';
import { convertDistance, DistanceUnit } from '@backend/lib-l10n-logic';
import type { FC } from 'react';

import type { SearchJobOfferOutput } from '~/business/JobOffer/searchJobOffers';
import { Link } from '~/components/Link';
import { PublishedDate } from '~/components/PublishedDate';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useCheckFeatureEnabled, useTranslation } from '~/hooks';
import { useMissionUrl } from '~/hooks/useMissionUrl';
import { formatSalary } from '~/utils/salary';

import { CardItem, CardTitle, IconWrapper, SecondaryText, Tag } from './MissionListCard.style';

type MissionListCardProps = {
  jobOffer: SearchJobOfferOutput;
  onMissionClick: (jobOffer: SearchJobOfferOutput) => void;
  showDistance: boolean;
};

export const MissionListCard: FC<MissionListCardProps> = ({ jobOffer, onMissionClick, showDistance }) => {
  const t = useTranslation();
  const { missionUrlPath } = useMissionUrl(jobOffer.slug);
  const { language, currency, distanceUnit } = useBusinessUnitContext();
  const publishedDateOnPreviewFeature = useCheckFeatureEnabled(Features.JobBoardPublishedDateOnPreview);

  const distance =
    jobOffer.distance < 1
      ? t('missions.close_distance')
      : t('missions.distance', {
          distance: convertDistance({ value: jobOffer.distance, unit: DistanceUnit.KM }, distanceUnit, { precision: 0 })
            .value,
        });

  return (
    <Link href={missionUrlPath}>
      <CardItem px="l" py="l" onClick={() => onMissionClick(jobOffer)}>
        <Flex.Column space="m">
          <CardTitle variant="heading-4" forwardedAs="h3">
            {jobOffer.title}
          </CardTitle>
          {jobOffer.city && (
            <Flex.Row space="s">
              <IconWrapper $intent="primary">
                <LocationFilledIcon size="xl" intent="primary" style={{ color: '#196CFF' }} />
              </IconWrapper>
              <Flex.Column space="s">
                <SecondaryText variant="body" color="disabled">
                  {[jobOffer.postalCode, jobOffer.city].filter(Boolean).join(' ')}
                </SecondaryText>
                {showDistance && <Text variant="body">{distance}</Text>}
              </Flex.Column>
            </Flex.Row>
          )}
          {jobOffer.salary?.unitRate && (
            <Flex.Row space="s">
              <IconWrapper $intent="warning">
                <DollarCircleFilledIcon size="xl" intent="warning" />
              </IconWrapper>
              <Flex.Column space="s">
                <SecondaryText variant="body" color="disabled">
                  {t(`salary.gross_per_unit.${jobOffer.salary.unitRate.toLowerCase()}`)}
                </SecondaryText>
                <Text variant="body">{formatSalary({ locale: language, salary: jobOffer.salary, currency })}</Text>
              </Flex.Column>
            </Flex.Row>
          )}
          <Flex.Row space="s">
            {jobOffer.urgent && <Tag variant="red" label={t('form_option_labels.urgent')} />}
            {jobOffer.type && (
              <Tag variant="grey" label={t(`form_option_labels.type.${jobOffer.type.toLowerCase()}`)} />
            )}
            {jobOffer.weeklyHours && (
              <Tag variant="grey" label={t('missions.weekly_hours.default', { hours: jobOffer.weeklyHours })} />
            )}
            {jobOffer.requiredExperience && (
              <Tag variant="grey" label={t(`tags.experience.${jobOffer.requiredExperience.toLowerCase()}`)} />
            )}
          </Flex.Row>
          {publishedDateOnPreviewFeature && (
            <Flex.Row space="s">
              <SecondaryText variant="body" color="disabled">
                <PublishedDate publishedAt={jobOffer.publishedAt} />
              </SecondaryText>
            </Flex.Row>
          )}
        </Flex.Column>
      </CardItem>
    </Link>
  );
};
